<script lang="ts">
  import Additem from '$icons/AddItemIcon.svelte';
  import { fade, fly, slide } from 'svelte/transition';
  import TickCircle from '$icons/TickCircleIcon.svelte';
  import { createEventDispatcher } from 'svelte';
  import type { IDetails, PostMedia, thumbnail } from 'garden-common';
  import Media from '../common/media/Media.svelte';

  export let media: PostMedia<IDetails<string>>[] = [];
  export let mediaIndex: number = 0;
  export let groupIndex: number = 0;
  export let hideButton: boolean = false;
  export let mode: 'normal' | 'group' = 'normal';

  let selected: [number, number] = [0, 0];
  let groupSelected: [number, number][] = [];

  $: if (mode === 'normal') groupSelected = [];

  const getName = (image: thumbnail) => {
    if (!image) {
      return 'No Media';
    } else if (typeof image === 'string') {
      return image.split(':')[3];
    } else {
      return image.name;
    }
  };

  const dispatch = createEventDispatcher();
</script>

<div class="flex flex-row gap-4 m-4 p-1 overflow-x-auto">
  {#each media as group, idxg}
    <div
      class="flex-row flex gap-2 rounded-lg p-[3px] h-full"
      class:hidden={group.images.length === 0}
      class:group={group.thumbnails?.length > 1 && idxg !== selected[0]}
      class:selected={idxg === selected[0]}
      class:create-mode={mode === 'group'}
    >
      {#each group.thumbnails as src, idx}
        <button
          class="relative flex items-center justify-center min-w-[100px] h-40 max-w-[100px] rounded-lg bg-black"
          id="image-item-{idx}-{idxg}"
          on:keydown
          on:click={() => {
            if (mode === 'group' && group.thumbnails.length === 1) {
              if (groupSelected.find(e => e[0] === idxg && e[1] === idx)) {
                groupSelected = groupSelected.filter(e => !(e[0] === idxg && e[1] === idx));
              } else {
                groupSelected.push([idxg, idx]);
                groupSelected = groupSelected;
              }

              dispatch('select', groupSelected);
            } else {
              if (idxg === selected[0] && idx === selected[1]) return;
              mediaIndex = idxg;
              groupIndex = idx;
              selected = [idxg, idx];
              dispatch('select', selected);
            }
          }}
        >
          {#if group.thumbnails.length === 1}
            {#if groupSelected.find(e => e[0] === idxg && e[1] === idx)}
              <div
                class="w-6 h-6 rounded-full bg-brand-green border-2 border-white absolute bottom-1 right-1 cursor-pointer flex items-center justify-center"
                class:hidden={!(mode === 'group')}
              >
                <TickCircle class="stroke-white" />
              </div>
            {:else}
              <div
                class="w-6 h-6 rounded-full bg-[#FFFFFF99] border border-[#808080] absolute bottom-1 right-1 cursor-pointer flex items-center justify-center"
                class:hidden={!(mode === 'group')}
              />
            {/if}
          {/if}
          {#if typeof src === 'string'}
            <Media class="w-full h-full object-cover rounded-10" {src} autoplay={false} />
          {:else}
            <Media
              class="w-full h-full object-cover rounded-10"
              src={URL.createObjectURL(src)}
              autoplay={false}
            />
          {/if}
        </button>
      {/each}
    </div>
  {/each}
  {#if !hideButton}
    {#if mode === 'normal'}
      <button
        class:h-40={media[0].images.length === 0}
        class="border-2 border-garden-grey-300 bg-white text-garden-grey-700 text-sm font-medium flex flex-row justify-center items-center gap-2 rounded-xl py-4 px-5 select-none cursor-pointer"
        on:keydown
        on:click={() => dispatch('additem')}
      >
        <Additem /> Add
      </button>
    {:else}
      <button
        class="h-40 border-2 border-garden-grey-300 bg-white text-grey-neutral-50 cursor-not-allowed text-sm font-medium flex flex-row justify-center items-center gap-2 rounded-xl py-4 px-5 select-none ease-out duration-200"
        class:create-group-button-selected={groupSelected.length > 1}
        on:keydown
        on:click={() => {
          if (groupSelected.length > 1) {
            dispatch('group-create', { location: groupSelected });
            groupSelected = [];
            mode = 'normal';
          }
        }}
      >
        Create
      </button>
    {/if}
  {/if}
</div>

<style type="postcss">
  .selected {
    @apply outline-brand-green outline outline-2 outline-offset-1;
  }

  .group {
    @apply relative outline-2 outline-dashed outline-accent-blue-60 rounded-xl flex;
  }

  .create-mode {
    @apply mb-8 gap-6 ease-out transition-all duration-200;
  }

  .create-group-button-selected {
    @apply text-brand-green cursor-pointer;
  }
</style>
